// The overlay's distance curve and time glide (docs/plans/basement-overlay.md). No DOM: tested in web/test.

import type { RoomOverlay } from '@lr3d/shared';

/** The distance (m) in plan from (x, z) to the overlay's trigger: its `centre` point, or the edge of its `area` (0 inside). */
export function overlayDistance(c: Pick<RoomOverlay, 'centre' | 'area'>, x: number, z: number): number {
  if (c.area) {
    const { min, max } = c.area;
    return Math.hypot(Math.max(min[0] - x, 0, x - max[0]), Math.max(min[1] - z, 0, z - max[1]));
  }
  const p = c.centre ?? [x, z];
  return Math.hypot(x - p[0], z - p[1]);
}

/**
 * How far into the effect a point (three.js x, z) is: 0 at or beyond `outer_m` from the trigger (centre or area), 1 at
 * or inside `inner_m`, smoothstep between (zero slope at both ends).
 */
export function overlayAmount(c: Pick<RoomOverlay, 'centre' | 'area' | 'outer_m' | 'inner_m'>, x: number, z: number): number {
  const d = overlayDistance(c, x, z);
  const t = Math.min(1, Math.max(0, (c.outer_m - d) / (c.outer_m - c.inner_m)));
  return t * t * (3 - 2 * t);
}

/** Longest time step one glide update takes (s): a long frame (a hidden tab) moves no further than this. */
export const GLIDE_MAX_DT = 0.1;

/** One step of an exponential glide from `cur` toward `target` over `dt` seconds with time constant `tau`. */
export function glide(cur: number, target: number, dt: number, tau: number): number {
  const a = 1 - Math.exp(-Math.min(GLIDE_MAX_DT, Math.max(0, dt)) / Math.max(1e-3, tau));
  return cur + (target - cur) * a;
}
